import { createContext } from 'react';
import * as PropTypes from 'prop-types';
import { getIn } from 'formik';
import type { FormContextType } from './apiForms';

/**
 * FormContext: значение — formik-контекст формы + транзитные пропсы Form,
 * тип-контракт — {@link FormContextType} (apiForms.d.ts).
 *
 * Default-значение — undefined, как в .jsx-версии (createContext() без аргумента):
 * потребители вне формы получают undefined, деструктуризация вне strictNullChecks.
 */
export const FormContext = createContext<FormContextType | undefined>(undefined);
FormContext.displayName = 'FormContext';

/**
 * Runtime-белый список пропсов «глухих» инпутов (SimpleStringInput, SimpleTimeInput):
 * фильтрует rest перед спредом в Form.Control — «мусорные» пропсы .jsx-потребителей
 * (size, maxlen, autoFocus и т.п.) не доходят до DOM. НЕ заменять на ...rest.
 */
export const inputPropTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  label: PropTypes.string,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  required: PropTypes.bool,
  maxLength: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.any,
  // Подсветка поля как «следующего для заполнения». Описание логики — CLAUDE.md → «Формы → Визуальные состояния поля».
  isNextField: PropTypes.bool,

  onChange: PropTypes.func,
  onInput: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func,
  onKeyPress: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onValidateFail: PropTypes.func,
  onCheckBoxLabelClick: PropTypes.func,
  onCheckBoxLabelValue: PropTypes.bool,
};

// @see https://github.com/jaredpalmer/formik/issues/1728
export const isRequiredField = (validationSchema: any, fieldName: string): boolean => {
  if (typeof validationSchema?.describe === 'function') {
    const schema = fieldName.split('.').reduce((acc, field) => {
      const tests = getIn(acc.fields, field);
      return tests || acc;
    }, validationSchema.describe());

    if (schema) {
      // ✅ Проверка 1: массив tests (Yup v0.32+, стандартный случай)
      if (schema.tests?.some(({ name }) => name === 'required')) {
        return true;
      }

      // ✅ Проверка 2: Set exclusiveTests (для массивов и некоторых типов)
      if (schema.exclusiveTests instanceof Set && schema.exclusiveTests.has('required')) {
        return true;
      }

      // ✅ Проверка 3: флаг optional === false (альтернативный способ маркировки required)
      if (schema.optional === false) {
        return true;
      }
    }
  }
  return false;
};

/**
 * @param data
 * @param titleKey
 * @param valueKey
 * @param sort
 * @param isDisabled
 * @return {{ value, label, disabled }[] | []}
 */
export const prepareSelectOptions = (
  data: any,
  titleKey?: any,
  valueKey?: any,
  sort?: any,
  isDisabled: boolean | ((item: any) => boolean) = false
) => {
  if (!data?.length) return [];

  if (sort) {
    // копия — иначе .sort() мутирует исходный массив (часто из Redux/MobX-стора)
    data = [...data];
    if (typeof sort === 'function') {
      data.sort(sort());
    } else {
      data.sort((a, b) => {
        let valA: any = String(a);
        let valB: any = String(b);

        if (typeof a === 'object') {
          valA = Object.keys(a);
          if (titleKey) {
            valA = (typeof titleKey === 'function') ? titleKey(a) : a[titleKey];
          }
        }

        if (typeof b === 'object') {
          valB = Object.keys(b);
          if (titleKey) {
            valB = (typeof titleKey === 'function') ? titleKey(b) : b[titleKey];
          }
        }

        return valA?.localeCompare(valB);
      });
    }
  }

  return data.map(item => {
    let value: any = Object.keys(item);

    if (valueKey) {
      value = (typeof valueKey === 'function') ? valueKey(item) : item[valueKey];
    }

    let label = item[value];

    if (titleKey) {
      label = (typeof titleKey === 'function') ? titleKey(item) : item[titleKey];
    }

    const disabled = (typeof isDisabled === 'function') ? isDisabled(item) : isDisabled;

    const option: any = { value, label, disabled };

    // Сохраняем цвет, если поле присутствует и является строкой
    if (item.color && typeof item.color === 'string') {
      option.color = item.color;
    }

    return option;
  });
};
